import { Box, Card, Collapse, List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import { History } from '@mui/icons-material'
import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import PaymentDetailsDialog from './PaymentDetailsDialog'

const OPERATION_LABELS = {
  INSERT: 'Création',
  UPDATE: 'Modification',
  DELETE: 'Suppression'
}

const OPERATION_COLORS = {
  INSERT: 'info',
  UPDATE: 'warning',
  DELETE: 'error'
}

function formatTimestamp(ts, isSmallScreen) {
  const d = new Date(ts)
  const date = d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  const time = d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
  return isSmallScreen ? `${date} ${time}` : `${date} à ${time}`
}

const adminSeatShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  seat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  rang: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  status: PropTypes.oneOf(['occupied', 'available', 'selected']).isRequired,
  selected_at: PropTypes.string,
  admin_data: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    paid: PropTypes.string
  }),
  booking: PropTypes.string,
  paymentDetails: PropTypes.shape({
    status: PropTypes.string,
    metadata: PropTypes.shape({
      show: PropTypes.string,
      schedule: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      selectedSeats: PropTypes.string,
      categories: PropTypes.string
    })
  })
})

function parseJSON(val) {
  if (val == null) return null
  if (typeof val === 'object') return val
  try {
    return JSON.parse(val)
  } catch {
    return val
  }
}

function flatObj(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  const result = {}
  for (const [key, val] of Object.entries(obj)) {
    if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      const nested = flatObj(val)
      if (typeof nested === 'object') {
        for (const [nk, nv] of Object.entries(nested)) {
          result[`${key}.${nk}`] = nv
        }
      } else {
        result[key] = nested
      }
    } else if (Array.isArray(val)) {
      result[key] = JSON.stringify(val)
    } else {
      result[key] = val
    }
  }
  return result
}

function dedupKeys(arrays) {
  const seen = {}
  const result = []
  for (const arr of arrays) {
    for (const key of arr) {
      if (!seen[key]) {
        seen[key] = true
        result.push(key)
      }
    }
  }
  return result
}

function displayValue(value, showData, key) {
  if (value == null) return '—'

  // Show ID resolution
  if (key === 'show_id' && typeof value === 'number' && Array.isArray(showData)) {
    const show = showData.find((s) => s.id === value)
    if (show) return `Spectacle : ${show.title}`
  }

  // Schedule ID resolution
  if (key === 'schedule_id' && typeof value === 'number' && Array.isArray(showData)) {
    for (const show of showData) {
      const sched = show.schedules?.find((s) => s.id === value)
      if (sched) return `Séance : ${sched.date} ${sched.time}`
    }
  }

  // Status
  if (key === 'status' && typeof value === 'number') {
    if (value === 0) return `Libre`
    if (value === 1) return `Sélectionné`
    if (value === 2) return `Occupé`
    return '-'
  }

  // Date detection
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      const d = new Date(value)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    } catch {
      return value
    }
  }

  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function isBookingValue(val) {
  return typeof val === 'string' && /^pi_/.test(val)
}

function BookingValue({ value, onClick }) {
  if (!isBookingValue(value)) {
    return (
      <Typography variant='caption' sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
    )
  }
  return (
    <Typography
      variant='caption'
      sx={{
        fontFamily: 'monospace',
        color: 'primary.main',
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:hover': { opacity: 0.8 }
      }}
      onClick={(e) => {
        e.stopPropagation()
        onClick(value)
      }}
    >
      {value}
    </Typography>
  )
}

BookingValue.propTypes = { value: PropTypes.any.isRequired, onClick: PropTypes.func.isRequired }

function DiffRow({ fieldKey, oldValue, newValue, isChanged, onBookingClick }) {
  const hasBooking = isBookingValue(oldValue) || isBookingValue(newValue)
  const bookingId = isBookingValue(oldValue) ? oldValue : isBookingValue(newValue) ? newValue : null

  return (
    <Box
      sx={{
        display: 'flex',
        py: 0.5,
        px: 0.75,
        bgcolor: hasBooking ? 'action.hover' : isChanged ? 'action.selected' : 'transparent',
        borderLeft: hasBooking ? 3 : isChanged ? 3 : 0,
        borderColor: hasBooking ? 'primary.main' : isChanged ? 'warning.main' : 'transparent',
        fontSize: '0.8125rem',
        cursor: hasBooking ? 'pointer' : 'default',
        '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' }
      }}
      onClick={hasBooking && bookingId ? () => onBookingClick(bookingId) : undefined}
    >
      <Typography variant='caption' sx={{ width: 250, fontFamily: 'monospace', color: 'text.secondary', flexShrink: 0 }}>
        {fieldKey}
      </Typography>
      <Box sx={{ width: 200 }}>
        <BookingValue value={oldValue} onClick={onBookingClick} />
      </Box>
      <Box sx={{ width: 200 }}>
        <BookingValue value={newValue} onClick={onBookingClick} />
      </Box>
    </Box>
  )
}

function AuditValueDiff({ oldContent, newContent, showData, showId, scheduleId, onBookingClick }) {
  const flatOld = oldContent ? flatObj(oldContent) : {}
  const flatNew = newContent ? flatObj(newContent) : {}
  const allKeys = dedupKeys([Object.keys(flatOld), Object.keys(flatNew)]).sort()

  // Resolve show/schedule display for header
  let showTitle = null
  let scheduleDisplay = null
  if (Array.isArray(showData) && showId != null) {
    const show = showData.find((s) => s.id === parseInt(showId))
    if (show) {
      showTitle = show.title
      if (scheduleId != null) {
        const sched = show.schedules?.find((s) => s.id === parseInt(scheduleId))
        if (sched) {
          scheduleDisplay = `${sched.date} ${sched.time}`
        }
      }
    }
  }

  return (
    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider', maxWidth: 750 }}>
      {(showTitle || scheduleDisplay) && (
        <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
          {showTitle ? `Spectacle : ${showTitle}` : ''}
          {showTitle && scheduleDisplay ? '  |  ' : ''}
          {scheduleDisplay ? `Séance : ${scheduleDisplay}` : ''}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontWeight: 650,
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          color: 'text.secondary'
        }}
      >
        <Box sx={{ width: 250 }}>Clé</Box>
        <Box sx={{ width: 200 }}>Ancien</Box>
        <Box sx={{ width: 200 }}>Nouveau</Box>
      </Box>
      {allKeys.map((key) => {
        const oldValue = displayValue(flatOld[key], showData, key)
        const newValue = displayValue(flatNew[key], showData, key)
        const isChanged = flatOld[key] !== flatNew[key]
        return (
          <DiffRow key={key} fieldKey={key} oldValue={oldValue} newValue={newValue} isChanged={isChanged} onBookingClick={onBookingClick} />
        )
      })}
    </Box>
  )
}

export default function AuditHistoryCard({ isSmallScreen, selectedSeat, showId, scheduleId }) {
  const [auditOpen, setAuditOpen] = React.useState(false)
  const [auditRecords, setAuditRecords] = React.useState([])
  const [auditLoading, setAuditLoading] = React.useState(false)
  const [showData, setShowData] = React.useState([])
  const [bookingOpen, setBookingOpen] = useState(false)
  const [bookingId, setBookingId] = useState(null)
  const [bookingDetails, setBookingDetails] = useState(null)
  const [bookingLoading, setBookingLoading] = useState(false)

  const handleHistoryClick = React.useCallback(async () => {
    if (auditOpen) {
      setAuditOpen(false)
      return
    }
    setAuditOpen(true)
    setAuditLoading(true)
    try {
      const { getSeatAuditHistory, fetchShows } = await import('../backend')
      const auditRes = await getSeatAuditHistory({ seat_id: selectedSeat.id, show_id: showId, schedule_id: scheduleId })
      const showsRes = await fetchShows()
      if (auditRes.success) {
        setAuditRecords(auditRes.data)
      }
      if (showsRes) {
        setShowData(showsRes)
      }
    } catch (e) {
      console.error('Failed to fetch audit history:', e)
    } finally {
      setAuditLoading(false)
    }
  }, [auditOpen, selectedSeat, showId, scheduleId])

  const handleBookingClick = useCallback(async (id) => {
    setBookingId(id)
    setBookingOpen(true)
    setBookingLoading(true)
    try {
      const { getBookingDetails } = await import('../backend')
      const res = await getBookingDetails(id)
      if (res.status) {
        setBookingDetails(res)
      }
    } catch (e) {
      console.error('Failed to fetch booking details:', e)
    } finally {
      setBookingLoading(false)
    }
  }, [])

  return (
    <>
      {auditLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Chargement de l&apos;historique...
          </Typography>
        </Box>
      ) : (
        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mb: 2,
            p: 2
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
            onClick={handleHistoryClick}
          >
            <History sx={{ color: 'text.secondary', fontSize: 22 }} />
            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
              Historique des modifications
            </Typography>
          </Stack>
          <Collapse in={auditOpen}>
            <List dense disablePadding>
              {auditRecords.length === 0
                ? "Pas d'historique disponible"
                : auditRecords.map((record) => (
                    <ListItem
                      key={record.id}
                      disablePadding
                      sx={{
                        display: 'block',
                        mb: 1,
                        px: 1,
                        borderLeft: 3,
                        borderColor: OPERATION_COLORS[record.operation] || 'text.secondary',
                        bgcolor: `${OPERATION_COLORS[record.operation]}.lighter`
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction='row' alignItems='center' spacing={1}>
                            <Typography variant='body2' fontWeight={600}>
                              {OPERATION_LABELS[record.operation] || record.operation}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              {' '}
                              — {formatTimestamp(record.changed_at, isSmallScreen)}
                            </Typography>
                            {record.changed_by && (
                              <Typography variant='caption' color='text.secondary'>
                                {' '}
                                par {record.changed_by}
                              </Typography>
                            )}
                          </Stack>
                        }
                        secondary={
                          <AuditValueDiff
                            oldContent={parseJSON(record.old_val)}
                            newContent={parseJSON(record.new_val)}
                            showData={showData}
                            showId={showId}
                            scheduleId={scheduleId}
                            onBookingClick={handleBookingClick}
                          />
                        }
                      />
                    </ListItem>
                  ))}
            </List>
          </Collapse>
        </Card>
      )}
      <PaymentDetailsDialog
        open={bookingOpen}
        bookingId={bookingId}
        paymentDetails={bookingDetails}
        loading={bookingLoading}
        onClose={() => setBookingOpen(false)}
      />
    </>
  )
}

AuditHistoryCard.propTypes = {
  isSmallScreen: PropTypes.bool.isRequired,
  selectedSeat: adminSeatShape.isRequired,
  showId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scheduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

DiffRow.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  oldValue: PropTypes.any,
  newValue: PropTypes.any,
  isChanged: PropTypes.bool,
  onBookingClick: PropTypes.func
}

AuditValueDiff.propTypes = {
  oldContent: PropTypes.any,
  newContent: PropTypes.any,
  showData: PropTypes.array,
  showId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scheduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBookingClick: PropTypes.func
}
